import React from 'react';
import { bool, func, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, Field } from 'react-final-form';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import * as validators from '../../util/validators';
import {
  Form,
  FieldTextInput,
  ImageFromFile,
  PrimaryButtonInline,
  ExternalLink,
  Icon,
} from '../../components';
import { getMediaUrl } from '../../util/uploadedMedia';

import css from './EditCreatorProfileForm.module.css';
import { pathForLocale } from '../../util/locale';

const ACCEPT_IMAGES = 'image/*';
const BIO_MAX_LENGTH = 1000;

export const EditCreatorProfileFormComponent = props => (
  <FinalForm
    {...props}
    // A refresh of the user record hands the form a new initialValues object.
    // Without this the form reinitialises and drops the picture just chosen.
    keepDirtyOnReinitialize
    render={fieldRenderProps => {
      const {
        className,
        disabled,
        handleSubmit,
        intl,
        invalid,
        pristine,
        updateInProgress,
        updateProfileError,
        uploadImageError,
        uploadImageInProgress,
        submitted,
        form,
        values,
      } = fieldRenderProps;

      const profileImage = values.profileImage;

      // Labels
      const nameLabel = intl.formatMessage({
        id: 'EditCreatorProfileForm.nameLabel',
      });

      // Bio field
      const bioLabel = intl.formatMessage({
        id: 'EditCreatorProfileForm.bioLabel',
      });
      const bioPlaceholder = intl.formatMessage({
        id: 'EditCreatorProfileForm.bioPlaceholder',
      });
      const bioMaxLengthMessage = intl.formatMessage(
        {
          id: 'EditCreatorProfileForm.bioMaxLength',
        },
        { maxLength: BIO_MAX_LENGTH }
      );
      const bioMaxLength = validators.maxLength(bioMaxLengthMessage, BIO_MAX_LENGTH);

      // Profile image components
      const imageFromFile =
        profileImage && profileImage.lastModified ? (
          <div className={css.uploadedImage}>
            <ImageFromFile
              id={profileImage.name}
              rootClassName={css.squareAspectRatio}
              file={profileImage}
            />
          </div>
        ) : null;

      const avatarComponent =
        profileImage && profileImage?.id ? (
          <div className={css.uploadedImage}>
            <div className={css.squareAspectRatio}>
              <img
                className={css.rootForImage}
                src={getMediaUrl(profileImage, 'card')}
                alt={profileImage.name}
              />
            </div>
          </div>
        ) : null;

      const chooseAvatarLabel =
        imageFromFile || avatarComponent ? (
          <div className={css.avatarContainer}>
            {imageFromFile}
            {avatarComponent}
            <div className={css.changeAvatar}>
              <Icon name="edit" size={16} className={css.changeAvatarIcon} />
              <FormattedMessage id="EditCreatorProfileForm.changeAvatar" />
            </div>
          </div>
        ) : (
          <div className={css.avatarPlaceholder}>
            <div className={css.avatarPlaceholderText}>
              <FormattedMessage id="EditCreatorProfileForm.addYourProfilePicture" />
            </div>
            <div className={css.avatarPlaceholderTextMobile}>
              <FormattedMessage id="EditCreatorProfileForm.addYourProfilePictureMobile" />
            </div>
          </div>
        );

      const uploadError = uploadImageError ? (
        <div className={css.error}>
          <FormattedMessage id="EditCreatorProfileForm.uploadImageFailed" />
        </div>
      ) : null;

      const submitError = updateProfileError ? (
        <div className={css.error}>
          <FormattedMessage id="EditCreatorProfileForm.updateProfileFailed" />
        </div>
      ) : null;

      const submitInProgress = updateInProgress || uploadImageInProgress;
      const submitDisabled = invalid || disabled || submitInProgress || pristine;

      const classes = classNames(css.root, className);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.formContent}>
            <h3 className={css.sectionTitle}>
              <FormattedMessage id="EditCreatorProfileForm.profileImageTitle" />
            </h3>
            <Field
              accept={ACCEPT_IMAGES}
              id="profileImage"
              name="profileImage"
              label={chooseAvatarLabel}
              type="file"
              form={null}
              disabled={submitInProgress}
            >
              {fieldProps => {
                const { accept, id, input, label, disabled: fieldDisabled } = fieldProps;
                const { name, type } = input;
                const onChange = async e => {
                  const file = e.target.files[0];
                  if (file) {
                    form.change('profileImage', file);
                    form.blur('profileImage');
                  }
                };

                return (
                  <div className={css.profileImageWrapper}>
                    <label className={css.label} htmlFor={id}>
                      {label}
                    </label>
                    <input
                      accept={accept}
                      id={id}
                      name={name}
                      className={css.profileImageInput}
                      disabled={fieldDisabled}
                      onChange={onChange}
                      type={type}
                    />
                  </div>
                );
              }}
            </Field>
            <p className={css.helpText}>
              <FormattedMessage id="EditCreatorProfileForm.profileImageHelp" />
            </p>
          </div>

          <div className={css.formContent}>
            <h3 className={css.sectionTitle}>
              <FormattedMessage id="EditCreatorProfileForm.aboutYouTitle" />
            </h3>
            <FieldTextInput
              className={css.nameField}
              type="text"
              id="name"
              name="name"
              label={nameLabel}
              disabled={true}
            />
            <p className={css.helpTextName}>
              <FormattedMessage
                id="EditCreatorProfileForm.nameHelp"
                values={{
                  profileSettingsLink: (
                    <ExternalLink href={pathForLocale('/account/profile-settings', intl.locale)}>
                      <FormattedMessage id="EditCreatorProfileForm.profileSettingsLink" />
                    </ExternalLink>
                  ),
                }}
              />
            </p>
            <FieldTextInput
              className={css.bio}
              type="textarea"
              id="bio"
              name="bio"
              label={bioLabel}
              placeholder={bioPlaceholder}
              validate={bioMaxLength}
            />
          </div>

          {uploadError}
          {submitError}

          <div className={css.editCreatorProfileFooter}>
            <div className={css.editCreatorProfileButtonsWrapper}>
              <PrimaryButtonInline
                size="small"
                checkmarkSize="small"
                className={css.editCreatorProfileSubmitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitted}
              >
                <FormattedMessage id="EditCreatorProfileForm.saveChanges" />
              </PrimaryButtonInline>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

EditCreatorProfileFormComponent.defaultProps = {
  className: null,
  updateProfileError: null,
  uploadImageError: null,
};

EditCreatorProfileFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  uploadImageInProgress: bool.isRequired,
  uploadImageError: propTypes.error,
  submitted: bool.isRequired,
  currentUser: propTypes.currentUser.isRequired,
};

const EditCreatorProfileForm = compose(injectIntl)(EditCreatorProfileFormComponent);

EditCreatorProfileForm.displayName = 'EditCreatorProfileForm';

export default EditCreatorProfileForm;
